export const SORT_MODES = ['wls', 'points', 'elims', 'wins', 'avgPlace'];
export const ANIMATION_STYLES = ['cascade', 'fade', 'slide', 'zoom', 'none'];
export const POSITION_INDICATOR_MODES = ['auto', 'always', 'never'];
export const FLAG_SHAPES = ['circle', 'rounded', 'square'];
export const RANK_STYLES = ['number', 'hash'];
export const VERTICAL_ALIGNS = ['center', 'top', 'bottom'];
export const NAME_CASES = ['uppercase', 'capitalize', 'none'];
export const HORIZONTAL_ALIGNS = ['center', 'left', 'right'];

const DEFAULT_THEME = {
    backgroundColor: '#22a6b3',
    backgroundImage: '',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundOverlayColor: 'transparent',
    fontFamily: 'Eurostile',
    fontFile: '',
    titleFontFamily: '',
    titleFontFile: '',
    rankFontFamily: 'GoodTimes',
    titleColor: '#ffffff',
    subtitleColor: 'rgba(255, 255, 255, 0.78)',
    textColor: '#ffffff',
    mutedTextColor: 'rgba(255, 255, 255, 0.65)',
    headerTextColor: '#ffffff',
    headerStartColor: '#1fb0c2',
    headerEndColor: '#0c6f7d',
    accentColor: '#4fe3ef',
    panelColor: 'rgba(4, 24, 27, 0.68)',
    panelBorderColor: '',
    rowColor: '#072a30',
    alternateRowColor: '#0a353d',
    rowBorderColor: '',
    aliveColor: '#ff3434',
    top1Color: '#ffd700',
    top2Color: '#d4d4d4',
    top3Color: '#e8a849',
    positiveColor: '#4caf50',
    negativeColor: '#f44336',
    neutralColor: '#666666',
    titleFontSize: 48,
    subtitleFontSize: 26,
    headerFontSize: 13,
    rowFontSize: 22,
    titleLetterSpacing: 2,
    headerLetterSpacing: 2,
    rowLetterSpacing: 0,
    textShadow: '1px 1px 4px rgba(0, 0, 0, 0.9)',
    titleTextShadow: '3px 3px 7px rgba(0, 0, 0, 0.85)',
    panelShadow: '0 0 50px rgba(0, 0, 0, 0.5)',
    logoImage: '',
    logoWidth: 220,
    logoTop: 24,
    logoAlign: 'center',
};

const DEFAULT_LABELS = {
    rank: 'PLACE',
    players: 'JOUEURS',
    wins: 'WINS',
    eliminations: 'ELIMS',
    points: 'POINTS',
    avgPlace: 'AVG PLACE',
    games: 'GAMES',
};

const DEFAULT_LAYOUT = {
    teamsPerPage: 10,
    columns: 1,
    titleTop: 40,
    tableTop: 80,
    canvasWidth: 1920,
    canvasHeight: 1080,
    scale: 1,
    autoScale: true,
    verticalAlign: 'center',
    columnGap: 40,
    cellGap: 0,
    rowHeight: 54,
    rowGap: 5,
    rowBorderWidth: 1,
    headerHeight: 48,
    rankWidth: 0,
    nameWidth: 0,
    statWidth: 0,
    panelPadding: 14,
    panelRadius: 14,
    rowRadius: 8,
};

const DEFAULT_DISPLAY = {
    showHeader: true,
    showPanel: true,
    showRank: true,
    showWins: true,
    winsAsColumn: false,
    showElims: true,
    showPoints: true,
    showAvgPlace: false,
    showGames: false,
    avgPlaceDecimals: 2,
    showRowAccent: true,
    rowGradient: true,
    stripedRows: true,
    highlightTop3: true,
    uppercaseNames: true,
    nameCase: 'uppercase',
    dimDeadRows: false,
    deadRowOpacity: 0.55,
    positionIndicators: 'auto',
    teamDetails: true,
    fillEmptyRows: false,
    flagShape: 'circle',
    flagSize: 0,
    pointsDecimals: 0,
    rankStyle: 'number',
    transparentBackground: false,
};

const DEFAULT_PAGINATION = {
    auto: false,
    intervalSeconds: 15,
    startRank: 1,
    maxTeams: 0,
};

const DEFAULT_ANIMATION = {
    style: 'cascade',
    durationMs: 550,
    staggerMs: 80,
};

export const DEFAULT_LEADERBOARD_CONFIG = {
    title: '',
    subtitle: '',
    leaderboardId: '',
    showFlags: false,
    showAliveIndicator: true,
    cascadeAnimation: true,
    refreshIntervalMs: 5000,
    forceRankByPoints: false,
    includeV7: true,
    indicatorsOnlyWhenAllDead: true,
    excludedSessionIds: [],
    customCss: '',
    labels: DEFAULT_LABELS,
    layout: DEFAULT_LAYOUT,
    display: DEFAULT_DISPLAY,
    pagination: DEFAULT_PAGINATION,
    animation: DEFAULT_ANIMATION,
    sort: { mode: 'wls' },
    theme: DEFAULT_THEME,
};

export const LEADERBOARD_DEFAULTS = {
    theme: DEFAULT_THEME,
    labels: DEFAULT_LABELS,
    layout: DEFAULT_LAYOUT,
    display: DEFAULT_DISPLAY,
    pagination: DEFAULT_PAGINATION,
    animation: DEFAULT_ANIMATION,
};

function clampInteger(value, fallback, min, max) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
}

function clampNumber(value, fallback, min, max) {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
}

function nonEmptyString(value, fallback) {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function optionalString(value, fallback = '') {
    return typeof value === 'string' ? value.trim() : fallback;
}

function normalizeBoolean(value, fallback) {
    return typeof value === 'boolean' ? value : fallback;
}

function oneOf(value, allowed, fallback) {
    const normalized = String(value || '').trim();
    return allowed.includes(normalized) ? normalized : fallback;
}

function plainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function validateLeaderboardSlug(slug) {
    const normalized = String(slug || '').trim().toLowerCase();
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
        throw new Error('Le slug du leaderboard est invalide.');
    }
    return normalized;
}

export function resolveLeaderboardAssetUrl(assetPath, configUrl) {
    if (!assetPath) return '';
    try {
        return new URL(assetPath, configUrl).toString();
    } catch (error) {
        return '';
    }
}

function normalizeSortMode(rawConfig) {
    const explicit = oneOf(plainObject(rawConfig.sort).mode, SORT_MODES, '');
    if (explicit) return explicit;
    return rawConfig.forceRankByPoints === true ? 'points' : 'wls';
}

function normalizePositionIndicatorMode(rawConfig, rawDisplay) {
    const explicit = oneOf(rawDisplay.positionIndicators, POSITION_INDICATOR_MODES, '');
    if (explicit) return explicit;
    return rawConfig.indicatorsOnlyWhenAllDead === false ? 'always' : 'auto';
}

function normalizeAnimation(rawConfig) {
    const rawAnimation = plainObject(rawConfig.animation);
    const legacyStyle = rawConfig.cascadeAnimation === false ? 'none' : 'cascade';
    return {
        style: oneOf(rawAnimation.style, ANIMATION_STYLES, legacyStyle),
        durationMs: clampInteger(rawAnimation.durationMs, DEFAULT_ANIMATION.durationMs, 0, 4000),
        staggerMs: clampInteger(rawAnimation.staggerMs, DEFAULT_ANIMATION.staggerMs, 0, 600),
    };
}

export function normalizeLeaderboardConfig(rawConfig, { slug, configUrl } = {}) {
    if (!rawConfig || typeof rawConfig !== 'object' || Array.isArray(rawConfig)) {
        throw new Error('La configuration du leaderboard doit être un objet JSON.');
    }

    const normalizedSlug = validateLeaderboardSlug(slug);
    const leaderboardId = String(rawConfig.leaderboardId || '').trim();
    if (leaderboardId && !/^\d{6,30}$/.test(leaderboardId)) {
        throw new Error('Le champ leaderboardId doit contenir uniquement des chiffres, entre 6 et 30.');
    }

    const rawTheme = plainObject(rawConfig.theme);
    const rawLabels = plainObject(rawConfig.labels);
    const rawLayout = plainObject(rawConfig.layout);
    const rawDisplay = plainObject(rawConfig.display);
    const rawPagination = plainObject(rawConfig.pagination);

    const columns = Number(rawLayout.columns) === 2 ? 2 : 1;
    const legacyTeamsPerPage = columns === 2
        ? clampInteger(rawLayout.rowsPerColumn, 10, 1, 30) * 2
        : clampInteger(rawLayout.firstPageRows, DEFAULT_LAYOUT.teamsPerPage, 1, 30);
    const teamsPerPage = Math.max(columns, clampInteger(
        rawLayout.teamsPerPage,
        legacyTeamsPerPage,
        1,
        60
    ));
    const refreshIntervalMs = rawConfig.refreshIntervalSeconds !== undefined
        ? clampInteger(rawConfig.refreshIntervalSeconds, 5, 2, 120) * 1000
        : clampInteger(
            rawConfig.refreshIntervalMs,
            DEFAULT_LEADERBOARD_CONFIG.refreshIntervalMs,
            2000,
            120000
        );

    const theme = {
        backgroundColor: nonEmptyString(rawTheme.backgroundColor, DEFAULT_THEME.backgroundColor),
        backgroundImage: resolveLeaderboardAssetUrl(rawTheme.backgroundImage, configUrl),
        backgroundPosition: nonEmptyString(rawTheme.backgroundPosition, DEFAULT_THEME.backgroundPosition),
        backgroundSize: nonEmptyString(rawTheme.backgroundSize, DEFAULT_THEME.backgroundSize),
        backgroundOverlayColor: nonEmptyString(rawTheme.backgroundOverlayColor, DEFAULT_THEME.backgroundOverlayColor),
        fontFamily: nonEmptyString(rawTheme.fontFamily, DEFAULT_THEME.fontFamily),
        fontFile: resolveLeaderboardAssetUrl(rawTheme.fontFile, configUrl),
        titleFontFamily: optionalString(rawTheme.titleFontFamily),
        titleFontFile: resolveLeaderboardAssetUrl(rawTheme.titleFontFile, configUrl),
        rankFontFamily: nonEmptyString(rawTheme.rankFontFamily, DEFAULT_THEME.rankFontFamily),
        titleColor: nonEmptyString(rawTheme.titleColor, DEFAULT_THEME.titleColor),
        subtitleColor: nonEmptyString(rawTheme.subtitleColor, DEFAULT_THEME.subtitleColor),
        textColor: nonEmptyString(rawTheme.textColor, DEFAULT_THEME.textColor),
        mutedTextColor: nonEmptyString(rawTheme.mutedTextColor, DEFAULT_THEME.mutedTextColor),
        headerTextColor: nonEmptyString(rawTheme.headerTextColor, DEFAULT_THEME.headerTextColor),
        headerStartColor: nonEmptyString(rawTheme.headerStartColor, DEFAULT_THEME.headerStartColor),
        headerEndColor: nonEmptyString(rawTheme.headerEndColor, DEFAULT_THEME.headerEndColor),
        accentColor: nonEmptyString(rawTheme.accentColor, DEFAULT_THEME.accentColor),
        panelColor: nonEmptyString(rawTheme.panelColor, DEFAULT_THEME.panelColor),
        panelBorderColor: optionalString(rawTheme.panelBorderColor),
        rowColor: nonEmptyString(rawTheme.rowColor, DEFAULT_THEME.rowColor),
        alternateRowColor: nonEmptyString(rawTheme.alternateRowColor, DEFAULT_THEME.alternateRowColor),
        rowBorderColor: optionalString(rawTheme.rowBorderColor),
        aliveColor: nonEmptyString(rawTheme.aliveColor, DEFAULT_THEME.aliveColor),
        top1Color: nonEmptyString(rawTheme.top1Color, DEFAULT_THEME.top1Color),
        top2Color: nonEmptyString(rawTheme.top2Color, DEFAULT_THEME.top2Color),
        top3Color: nonEmptyString(rawTheme.top3Color, DEFAULT_THEME.top3Color),
        positiveColor: nonEmptyString(rawTheme.positiveColor, DEFAULT_THEME.positiveColor),
        negativeColor: nonEmptyString(rawTheme.negativeColor, DEFAULT_THEME.negativeColor),
        neutralColor: nonEmptyString(rawTheme.neutralColor, DEFAULT_THEME.neutralColor),
        titleFontSize: clampInteger(rawTheme.titleFontSize, DEFAULT_THEME.titleFontSize, 16, 160),
        subtitleFontSize: clampInteger(rawTheme.subtitleFontSize, DEFAULT_THEME.subtitleFontSize, 10, 90),
        headerFontSize: clampInteger(rawTheme.headerFontSize, DEFAULT_THEME.headerFontSize, 8, 40),
        rowFontSize: clampInteger(rawTheme.rowFontSize, DEFAULT_THEME.rowFontSize, 10, 48),
        titleLetterSpacing: clampNumber(rawTheme.titleLetterSpacing, DEFAULT_THEME.titleLetterSpacing, -5, 20),
        headerLetterSpacing: clampNumber(rawTheme.headerLetterSpacing, DEFAULT_THEME.headerLetterSpacing, -3, 12),
        rowLetterSpacing: clampNumber(rawTheme.rowLetterSpacing, DEFAULT_THEME.rowLetterSpacing, -3, 12),
        textShadow: optionalString(rawTheme.textShadow, DEFAULT_THEME.textShadow) || 'none',
        titleTextShadow: optionalString(rawTheme.titleTextShadow, DEFAULT_THEME.titleTextShadow) || 'none',
        panelShadow: optionalString(rawTheme.panelShadow, DEFAULT_THEME.panelShadow) || 'none',
        logoImage: resolveLeaderboardAssetUrl(rawTheme.logoImage, configUrl),
        logoWidth: clampInteger(rawTheme.logoWidth, DEFAULT_THEME.logoWidth, 40, 900),
        logoTop: clampInteger(rawTheme.logoTop, DEFAULT_THEME.logoTop, 0, 900),
        logoAlign: oneOf(rawTheme.logoAlign, HORIZONTAL_ALIGNS, DEFAULT_THEME.logoAlign),
    };

    const sortMode = normalizeSortMode(rawConfig);

    return {
        slug: normalizedSlug,
        title: optionalString(rawConfig.title),
        subtitle: optionalString(rawConfig.subtitle),
        leaderboardId,
        showFlags: normalizeBoolean(rawConfig.showFlags, DEFAULT_LEADERBOARD_CONFIG.showFlags),
        showAliveIndicator: normalizeBoolean(
            rawConfig.showAliveIndicator,
            DEFAULT_LEADERBOARD_CONFIG.showAliveIndicator
        ),
        cascadeAnimation: normalizeBoolean(
            rawConfig.cascadeAnimation,
            DEFAULT_LEADERBOARD_CONFIG.cascadeAnimation
        ),
        refreshIntervalMs,
        forceRankByPoints: sortMode === 'points',
        sortMode,
        includeV7: normalizeBoolean(rawConfig.includeV7, DEFAULT_LEADERBOARD_CONFIG.includeV7),
        indicatorsOnlyWhenAllDead: normalizeBoolean(
            rawConfig.indicatorsOnlyWhenAllDead,
            DEFAULT_LEADERBOARD_CONFIG.indicatorsOnlyWhenAllDead
        ),
        excludedSessionIds: Array.isArray(rawConfig.excludedSessionIds)
            ? rawConfig.excludedSessionIds.map((value) => String(value)).filter(Boolean)
            : [],
        customCss: typeof rawConfig.customCss === 'string' ? rawConfig.customCss.slice(0, 20000) : '',
        labels: {
            rank: nonEmptyString(rawLabels.rank, DEFAULT_LABELS.rank),
            players: nonEmptyString(rawLabels.players, DEFAULT_LABELS.players),
            wins: nonEmptyString(rawLabels.wins, DEFAULT_LABELS.wins),
            eliminations: nonEmptyString(rawLabels.eliminations, DEFAULT_LABELS.eliminations),
            points: nonEmptyString(rawLabels.points, DEFAULT_LABELS.points),
            avgPlace: nonEmptyString(rawLabels.avgPlace, DEFAULT_LABELS.avgPlace),
            games: nonEmptyString(rawLabels.games, DEFAULT_LABELS.games),
        },
        layout: {
            teamsPerPage,
            columns,
            titleTop: clampInteger(rawLayout.titleTop, DEFAULT_LAYOUT.titleTop, 0, 300),
            tableTop: clampInteger(rawLayout.tableTop, DEFAULT_LAYOUT.tableTop, 0, 400),
            canvasWidth: clampInteger(rawLayout.canvasWidth, DEFAULT_LAYOUT.canvasWidth, 480, 3840),
            canvasHeight: clampInteger(rawLayout.canvasHeight, DEFAULT_LAYOUT.canvasHeight, 270, 2160),
            scale: clampNumber(rawLayout.scale, DEFAULT_LAYOUT.scale, 0.3, 2.5),
            autoScale: normalizeBoolean(rawLayout.autoScale, DEFAULT_LAYOUT.autoScale),
            verticalAlign: oneOf(rawLayout.verticalAlign, VERTICAL_ALIGNS, DEFAULT_LAYOUT.verticalAlign),
            columnGap: clampInteger(rawLayout.columnGap, DEFAULT_LAYOUT.columnGap, 0, 400),
            cellGap: clampInteger(rawLayout.cellGap, DEFAULT_LAYOUT.cellGap, 0, 80),
            rowHeight: clampInteger(rawLayout.rowHeight, DEFAULT_LAYOUT.rowHeight, 24, 140),
            rowGap: clampInteger(rawLayout.rowGap, DEFAULT_LAYOUT.rowGap, 0, 40),
            rowBorderWidth: clampInteger(rawLayout.rowBorderWidth, DEFAULT_LAYOUT.rowBorderWidth, 0, 12),
            headerHeight: clampInteger(rawLayout.headerHeight, DEFAULT_LAYOUT.headerHeight, 0, 120),
            rankWidth: clampInteger(rawLayout.rankWidth, DEFAULT_LAYOUT.rankWidth, 0, 400),
            nameWidth: clampInteger(rawLayout.nameWidth, DEFAULT_LAYOUT.nameWidth, 0, 1400),
            statWidth: clampInteger(rawLayout.statWidth, DEFAULT_LAYOUT.statWidth, 0, 400),
            panelPadding: clampInteger(rawLayout.panelPadding, DEFAULT_LAYOUT.panelPadding, 0, 80),
            panelRadius: clampInteger(rawLayout.panelRadius, DEFAULT_LAYOUT.panelRadius, 0, 60),
            rowRadius: clampInteger(rawLayout.rowRadius, DEFAULT_LAYOUT.rowRadius, 0, 40),
        },
        display: {
            showHeader: normalizeBoolean(rawDisplay.showHeader, DEFAULT_DISPLAY.showHeader),
            showPanel: normalizeBoolean(rawDisplay.showPanel, DEFAULT_DISPLAY.showPanel),
            showRank: normalizeBoolean(rawDisplay.showRank, DEFAULT_DISPLAY.showRank),
            showWins: normalizeBoolean(rawDisplay.showWins, DEFAULT_DISPLAY.showWins),
            winsAsColumn: normalizeBoolean(rawDisplay.winsAsColumn, DEFAULT_DISPLAY.winsAsColumn),
            showElims: normalizeBoolean(rawDisplay.showElims, DEFAULT_DISPLAY.showElims),
            showPoints: normalizeBoolean(rawDisplay.showPoints, DEFAULT_DISPLAY.showPoints),
            showAvgPlace: normalizeBoolean(rawDisplay.showAvgPlace, DEFAULT_DISPLAY.showAvgPlace),
            showGames: normalizeBoolean(rawDisplay.showGames, DEFAULT_DISPLAY.showGames),
            avgPlaceDecimals: clampInteger(rawDisplay.avgPlaceDecimals, DEFAULT_DISPLAY.avgPlaceDecimals, 0, 3),
            showRowAccent: normalizeBoolean(rawDisplay.showRowAccent, DEFAULT_DISPLAY.showRowAccent),
            rowGradient: normalizeBoolean(rawDisplay.rowGradient, DEFAULT_DISPLAY.rowGradient),
            stripedRows: normalizeBoolean(rawDisplay.stripedRows, DEFAULT_DISPLAY.stripedRows),
            highlightTop3: normalizeBoolean(rawDisplay.highlightTop3, DEFAULT_DISPLAY.highlightTop3),
            uppercaseNames: normalizeBoolean(rawDisplay.uppercaseNames, DEFAULT_DISPLAY.uppercaseNames),
            nameCase: oneOf(
                rawDisplay.nameCase,
                NAME_CASES,
                rawDisplay.uppercaseNames === false ? 'none' : DEFAULT_DISPLAY.nameCase
            ),
            dimDeadRows: normalizeBoolean(rawDisplay.dimDeadRows, DEFAULT_DISPLAY.dimDeadRows),
            deadRowOpacity: clampNumber(rawDisplay.deadRowOpacity, DEFAULT_DISPLAY.deadRowOpacity, 0.1, 1),
            positionIndicators: normalizePositionIndicatorMode(rawConfig, rawDisplay),
            teamDetails: normalizeBoolean(rawDisplay.teamDetails, DEFAULT_DISPLAY.teamDetails),
            fillEmptyRows: normalizeBoolean(rawDisplay.fillEmptyRows, DEFAULT_DISPLAY.fillEmptyRows),
            flagShape: oneOf(rawDisplay.flagShape, FLAG_SHAPES, DEFAULT_DISPLAY.flagShape),
            flagSize: clampInteger(rawDisplay.flagSize, DEFAULT_DISPLAY.flagSize, 0, 60),
            pointsDecimals: clampInteger(rawDisplay.pointsDecimals, DEFAULT_DISPLAY.pointsDecimals, 0, 3),
            rankStyle: oneOf(rawDisplay.rankStyle, RANK_STYLES, DEFAULT_DISPLAY.rankStyle),
            transparentBackground: normalizeBoolean(
                rawDisplay.transparentBackground,
                DEFAULT_DISPLAY.transparentBackground
            ),
        },
        pagination: {
            auto: normalizeBoolean(rawPagination.auto, DEFAULT_PAGINATION.auto),
            intervalSeconds: clampInteger(
                rawPagination.intervalSeconds,
                DEFAULT_PAGINATION.intervalSeconds,
                3,
                600
            ),
            startRank: clampInteger(rawPagination.startRank, DEFAULT_PAGINATION.startRank, 1, 500),
            maxTeams: clampInteger(rawPagination.maxTeams, DEFAULT_PAGINATION.maxTeams, 0, 500),
        },
        animation: normalizeAnimation(rawConfig),
        sort: { mode: sortMode },
        theme,
    };
}

export function getLeaderboardConfigUrl(slug, pageUrl = window.location.href) {
    const normalizedSlug = validateLeaderboardSlug(slug);
    const publicRoot = process.env.PUBLIC_URL || '/';
    const rootUrl = new URL(publicRoot.endsWith('/') ? publicRoot : `${publicRoot}/`, pageUrl);
    return new URL(`leaderboards/${normalizedSlug}/config.json`, rootUrl).toString();
}

export async function loadLeaderboardConfig(slug, { signal, pageUrl } = {}) {
    const configUrl = getLeaderboardConfigUrl(slug, pageUrl);
    const response = await fetch(configUrl, { signal, cache: 'no-store' });
    if (!response.ok) {
        if (response.status === 404) {
            throw new Error(`Le leaderboard « ${slug} » n'existe pas.`);
        }
        throw new Error(`Impossible de charger la configuration (${response.status}).`);
    }

    const rawConfig = await response.json();
    return normalizeLeaderboardConfig(rawConfig, { slug, configUrl: response.url || configUrl });
}
